import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { IconWithFallbackProps } from "Global/IconWithFallback";
import TextWithIcon from "common/Text/TextWithIcon";
import { checkSymbolSearch } from "@shared/utils/misc";
import DisconnectOutlined from "@ant-design/icons/DisconnectOutlined";
import DownOutlined from "@ant-design/icons/DownOutlined";
import { useTheme } from "styled-components";
import Flex from "common/Boxes/Flex";
import { useInvalidAccount } from "@shared/features/settings/hooks";
import { useBottomNavigator } from "utils/hooks/misc";
import FiltersSelect from "common/Filters/common/FiltersSelect.tsx";
import { SelectProps } from "antd/es/select";

interface MultiItemSelectProps {
  allItems: string[];
  selectedItems: string[];
  onChange: (items: string[]) => void;
  placeholder?: string | ReactNode;
  disabled?: boolean;
  getIconProps?: (value: string) => IconWithFallbackProps;
  getAccountId?: (value: string) => number | undefined;
  containerID?: string;
  style?: CSSProperties;
  open?: boolean;
  maxTagPlaceholder?: (numSelected: number) => ReactNode;
  popupMatchSelectWidth?: boolean | number;
  renderOption?: (option: string) => ReactNode;
  isMultiple?: boolean;
  isSingleRowView?: boolean;
  width?: number;
  title?: string;
  bottomExtraElement?: React.JSX.Element;
  allSelected?: boolean;
  popupRender?: SelectProps["popupRender"];
}

const MultiItemSelect = ({
  allItems,
  selectedItems,
  onChange,
  disabled,
  placeholder,
  getIconProps,
  getAccountId,
  containerID,
  style,
  maxTagPlaceholder,
  popupMatchSelectWidth,
  renderOption,
  isMultiple,
  isSingleRowView,
  width,
  title,
  bottomExtraElement,
  allSelected,
  popupRender,
}: MultiItemSelectProps) => {
  const theme = useTheme();
  const { getInvalidAccountData } = useInvalidAccount();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const allowClear = !isBottomNavigator && isMultiple;

  const memoizedSortedOptions = useMemo(() => {
    const selectedItemsSet = new Set<string | number>(selectedItems);
    const selectedSymbols: string[] = [];
    const notSelectedSymbols: string[] = [];
    allItems.forEach((symbol) => {
      if (selectedItemsSet.has(symbol)) selectedSymbols.push(symbol);
      else notSelectedSymbols.push(symbol);
    });
    return [...selectedSymbols, ...notSelectedSymbols].map((item) => {
      const accountId = getAccountId?.(item);
      const invalidAccountData = getInvalidAccountData(accountId);
      let text: ReactNode = item;
      let label: ReactNode = item;
      if (renderOption) {
        label = renderOption(item);
        text = label;
      }
      if (getIconProps) {
        label = (
          <TextWithIcon
            text={label}
            {...getIconProps(item)}
            textStyle={{
              color: invalidAccountData.color ?? "inherit",
            }}
            style={{ paddingRight: 8 }}
          />
        );
      }
      return {
        text,
        label: (
          <Flex $justifyContent="flex-start">
            {label}
            {!!invalidAccountData.color && (
              <DisconnectOutlined
                style={{ color: invalidAccountData.color, marginLeft: 6 }}
              />
            )}
          </Flex>
        ),
        value: item,
      };
    });
  }, [selectedItems, allItems, theme, getInvalidAccountData, getAccountId]);

  const [symbolsSelectOpen, setSymbolsSelectOpen] = useState(false);
  const [sortedOptions, setSortedOptions] = useState<Option[]>(
    memoizedSortedOptions
  );
  const optionsChanged = sortedOptions.length !== allItems.length;

  useEffect(() => {
    if (!symbolsSelectOpen || optionsChanged) {
      setSortedOptions(memoizedSortedOptions);
    }
  }, [symbolsSelectOpen, memoizedSortedOptions, optionsChanged]);

  return (
    <FiltersSelect
      mode={isMultiple ? "multiple" : undefined}
      placeholder={placeholder || `Choose...`}
      onChange={(items) => {
        onChange(Array.isArray(items) ? items : [items]);
      }}
      maxTagCount={0}
      value={selectedItems}
      optionLabelProp="label"
      virtual={true}
      alwaysShowClear={true}
      style={{
        width: "100%",
        ...style,
      }}
      options={sortedOptions}
      open={symbolsSelectOpen}
      onOpenChange={setSymbolsSelectOpen}
      showSearch={{
        filterOption: (searchValue, option) => {
          const optionSearchValue =
            typeof option?.text === "string" ? option?.text : option?.value;
          return checkSymbolSearch(optionSearchValue, searchValue);
        },
      }}
      error={!selectedItems.length}
      disabled={disabled}
      getPopupContainer={
        containerID ? () => document.getElementById(containerID)! : undefined
      }
      popupMatchSelectWidth={popupMatchSelectWidth}
      maxTagPlaceholder={(items) => maxTagPlaceholder?.(items.length)}
      suffixIcon={isSingleRowView ? <></> : <DownOutlined />}
      mobileTitle={title}
      bottomExtraElement={bottomExtraElement}
      allowClear={allowClear}
      popupRender={popupRender}
      $singleRow={isSingleRowView}
      $width={width}
      $allSelected={allSelected}
      $isBottomNavigator={isBottomNavigator}
      $allowClear={allowClear}
    />
  );
};

export default MultiItemSelect;

type Option = { label: ReactNode; value: string; text: ReactNode };
